import { z } from "zod";
import { DocumentPatchOperationSchema } from "./documents.js";
import { ProposalCommandSchema } from "./proposals.js";
import { DecimalStringSchema, Rfc3339MillisSchema, Sha256HexSchema, UuidSchema } from "./primitives.js";
const LimitSchema = z.number().int().min(1).max(100);
const CursorSchema = z.string().min(1).max(512).nullable().optional();
const ListProjectsArgumentsSchema = z.object({ limit: LimitSchema.optional(), cursor: CursorSchema }).strict();
const SearchNotesArgumentsSchema = z.object({ query: z.string().trim().min(1).max(500), limit: LimitSchema.optional(), cursor: CursorSchema }).strict();
const GetNoteArgumentsSchema = z.object({ noteId: UuidSchema }).strict();
const ProposeCaptureNoteArgumentsSchema = z.object({
    title: z.string().trim().min(1).max(500),
    bodyMarkdown: z.string().max(1_048_576),
    reason: z.string().trim().min(1).max(4_096),
}).strict();
const ListTasksArgumentsSchema = z.object({ projectId: UuidSchema, limit: LimitSchema.optional(), cursor: CursorSchema }).strict();
const CreateProposalArgumentsSchema = z.object({
    command: ProposalCommandSchema,
    reason: z.string().trim().min(1).max(4_096),
}).strict();
const ListDocumentsArgumentsSchema = z.object({ workspaceId: UuidSchema, limit: LimitSchema.optional(), cursor: CursorSchema }).strict();
const ReadDocumentArgumentsSchema = z.object({ documentId: UuidSchema, versionId: UuidSchema.nullable().optional() }).strict();
const StageDocumentChangesArgumentsSchema = z.object({
    documentId: UuidSchema,
    baseVersionId: UuidSchema,
    baseSha256: Sha256HexSchema,
    operations: z.array(DocumentPatchOperationSchema).min(1).max(1_000),
    reason: z.string().trim().min(1).max(4_096),
}).strict();
const GetJobArgumentsSchema = z.object({ jobId: UuidSchema }).strict();
const CancelJobArgumentsSchema = z.object({ jobId: UuidSchema, expectedLeaseGeneration: z.string().regex(/^(?:0|[1-9][0-9]*)$/) }).strict();
export const McpToolCallSchema = z.discriminatedUnion("tool", [
    z.object({ tool: z.literal("mind_list_projects"), arguments: ListProjectsArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_search_notes"), arguments: SearchNotesArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_get_note"), arguments: GetNoteArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_propose_capture_note"), arguments: ProposeCaptureNoteArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_list_tasks"), arguments: ListTasksArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_create_proposal"), arguments: CreateProposalArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_list_documents"), arguments: ListDocumentsArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_read_document"), arguments: ReadDocumentArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_stage_document_changes"), arguments: StageDocumentChangesArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_get_job"), arguments: GetJobArgumentsSchema }).strict(),
    z.object({ tool: z.literal("mind_cancel_job"), arguments: CancelJobArgumentsSchema }).strict(),
]);
export const McpAccessTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const CreateMcpAccessTokenSchema = z.object({
    workspaceId: UuidSchema,
    label: z.string().trim().min(1).max(100),
    expiresInSeconds: z.number().int().min(60).max(43_200),
}).strict();
export const McpAccessTokenCreatedSchema = z.object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    token: McpAccessTokenSchema,
    expiresAt: Rfc3339MillisSchema,
}).strict();
export const McpAccessTokenMetadataSchema = z.object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    label: z.string().min(1).max(100),
    grantEpoch: DecimalStringSchema,
    createdAt: Rfc3339MillisSchema,
    expiresAt: Rfc3339MillisSchema,
    revokedAt: Rfc3339MillisSchema.nullable(),
    lastUsedAt: Rfc3339MillisSchema.nullable(),
}).strict();
export const McpAccessTokenListSchema = z.object({
    items: z.array(McpAccessTokenMetadataSchema).max(4_096),
}).strict();
export const McpDaemonRequestSchema = z.object({
    requestId: UuidSchema,
    accessToken: McpAccessTokenSchema,
    call: McpToolCallSchema,
}).strict();
export const McpDaemonSuccessSchema = z.object({
    requestId: UuidSchema,
    ok: z.literal(true),
    result: z.unknown(),
}).strict();
export const McpDaemonFailureSchema = z.object({
    requestId: UuidSchema,
    ok: z.literal(false),
    error: z.enum(["MCP_REQUEST_REJECTED", "MCP_UNAVAILABLE", "MCP_REVOKED", "MCP_RATE_LIMITED"]),
}).strict();
export const McpDaemonResponseSchema = z.union([McpDaemonSuccessSchema, McpDaemonFailureSchema]);
export const MCP_PROTOCOL_VERSION = "2025-11-25";
export const MCP_TOOL_NAMES = [
    "mind_list_projects",
    "mind_search_notes",
    "mind_get_note",
    "mind_propose_capture_note",
    "mind_list_tasks",
    "mind_create_proposal",
    "mind_list_documents",
    "mind_read_document",
    "mind_stage_document_changes",
    "mind_get_job",
    "mind_cancel_job",
];
export const McpToolNameSchema = z.enum(MCP_TOOL_NAMES);
//# sourceMappingURL=mcp.js.map